import type { FacilityDetails } from '@gyym/sdk'

import { getImageUrl } from '@/utils/getImageUrl'

import { GYYM_SAME_AS, MAIN_ENTITY_OF_PAGE } from '../constants'
import { safeJoin, sanitizeString } from '../sanitize'
import type { FacilityItemPage, ImageObject } from '../types'
import { buildBreadcrumb } from './buildBreadcrumb'
import { buildExerciseGym } from './buildExerciseGym'
import { buildMentions } from './buildMentions'

export interface BuildFacilityJsonLdParams {
  facility: FacilityDetails
  prefecture: { name: string; slug: string }
  city: { code: number; name: string }
  canonicalUrl: string
  keywords: string[]
}

/**
 * Build complete JSON-LD structured data for facility detail page
 * Returns ItemPage with ExerciseGym as mainEntity
 */
export const buildFacilityJsonLd = ({
  facility,
  prefecture,
  city,
  canonicalUrl,
  keywords,
}: BuildFacilityJsonLdParams): FacilityItemPage => {
  // Build breadcrumb
  const breadcrumb = buildBreadcrumb({ facility, prefecture, city })

  // Build main entity (ExerciseGym)
  const mainEntity = buildExerciseGym({
    facility,
    prefecture,
    city,
    canonicalUrl,
  })

  // Build primary image with absolute URL
  const firstImage = facility.images?.[0]
  const primaryImageOfPage: ImageObject | undefined = firstImage
    ? {
        '@type': 'ImageObject',
        url: getImageUrl(firstImage),
        name: facility.name,
      }
    : undefined

  // Build images array for ItemPage
  const images: ImageObject[] = facility.images.map((url) => ({
    '@type': 'ImageObject',
    url: getImageUrl(url),
  }))

  // Build description
  const attentionText =
    facility.attention && facility.attention.length > 0
      ? facility.attention.join(' ')
      : ''
  const description = sanitizeString(
    safeJoin([facility.description, attentionText], ' '),
  )

  // Build genre from activities
  const genre = facility.activities.map((activity) => activity.name)

  // Build mentions
  const mentions = buildMentions({
    tags: facility.tags,
    activities: facility.activities,
    station: facility.station,
  })

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemPage',
    url: canonicalUrl,
    headline: facility.name,
    genre,
    keywords,
    description,
    breadcrumb,
    primaryImageOfPage,
    mainEntity,
    mentions: mentions.length > 0 ? mentions : undefined,
    image: images.length > 0 ? images : undefined,
    sameAs: [...GYYM_SAME_AS],
    mainEntityOfPage: MAIN_ENTITY_OF_PAGE,
  }
}

export { buildAmenityFeature } from './buildAmenityFeature'
export { buildBreadcrumb } from './buildBreadcrumb'
export { buildExerciseGym } from './buildExerciseGym'
export { buildMentions } from './buildMentions'
export { buildOfferCatalog } from './buildOfferCatalog'
export { buildReviews } from './buildReviews'
